import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";

const TestDrive = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const vehicleId = params.get("vehicle");
  const [vehicle, setVehicle] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", preferred_date: "", preferred_time: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (vehicleId) api.get(`/vehicles/${vehicleId}`).then((r) => setVehicle(r.data)).catch(() => {});
  }, [vehicleId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) { toast.error("Please fill required fields"); return; }
    setBusy(true);
    try {
      await api.post("/test-drives", { ...form, vehicle_id: vehicleId || null });
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong");
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="container-wide py-24 text-center max-w-xl mx-auto" data-testid="td-success">
        <div className="overline text-[#7A1519]">Test Drive Requested</div>
        <h1 className="font-display text-3xl md:text-4xl mt-3 font-semibold">Test Drive Request Received</h1>
        <p className="mt-4 text-[#242424]/75">
          Thank you. An Autobahn Cars advisor will call or WhatsApp you shortly to confirm a mutually convenient slot.
        </p>
        <div className="mt-6 flex gap-3 justify-center">
          <Link to="/cars" className="border hairline px-6 py-3 rounded-sm text-sm">Continue browsing</Link>
          <Link to="/" className="bg-[#7A1519] text-white px-6 py-3 rounded-sm text-sm">Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-wide py-16 max-w-2xl mx-auto" data-testid="page-test-drive">
      <button onClick={() => nav(-1)} className="text-sm text-[#242424]/70 hover:text-[#7A1519] inline-flex items-center gap-2"><ArrowLeft size={14} /> Back</button>
      <div className="mt-6 overline text-[#7A1519]">Book Test Drive</div>
      <h1 className="font-display text-3xl md:text-5xl mt-2 font-semibold tracking-tighter">
        {vehicle ? `${vehicle.manufacturing_year} ${vehicle.brand} ${vehicle.model}` : "Book a test drive"}
      </h1>
      {vehicle && <div className="text-sm text-[#242424]/70 mt-1">{vehicle.variant}</div>}

      <form onSubmit={submit} className="mt-10 bg-white border hairline p-8 rounded-sm space-y-4">
        <Field label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="td-name" />
        <Field label="Phone *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="td-phone" />
        <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="td-email" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Preferred Date" type="date" value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} testId="td-date" />
          <Field label="Preferred Time" type="time" value={form.preferred_time} onChange={(v) => setForm({ ...form, preferred_time: v })} testId="td-time" />
        </div>
        <div>
          <label className="overline text-[#242424]/60 block mb-2">Message</label>
          <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#7A1519]" data-testid="td-message" />
        </div>
        <button type="submit" disabled={busy} className="w-full bg-[#7A1519] hover:bg-[#5a0f12] text-white py-3.5 rounded-sm text-sm font-semibold tracking-wide disabled:opacity-60" data-testid="td-submit">
          {busy ? "Sending…" : "Request Test Drive"}
        </button>
        <div className="text-xs text-[#242424]/60 leading-relaxed">Submitting this request does not automatically guarantee your requested slot — an advisor will contact you to confirm.</div>
      </form>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <label className="overline text-[#242424]/60 block mb-2">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#7A1519]" data-testid={testId} />
  </div>
);

export default TestDrive;
